import React from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';

interface PinEntryProps {
  title: string;
  /**
   * Called with four digits. Returns the message to show when they are not
   * accepted, or null once the caller has signed the person in.
   */
  onSubmit: (pinCode: string) => string | null;
  /** Shown under the button, e.g. while the officials list has not arrived. */
  notice?: string | null;
}

/**
 * The four-digit PIN screen, shared by the adjudicator and the chairperson
 * logins. It only collects the digits: who they belong to, and what signing in
 * means, is the caller's business.
 */
const PinEntry: React.FC<PinEntryProps> = ({ title, onSubmit, notice }) => {
  const [pinCode, setPinCode] = React.useState('');
  const [error, setError] = React.useState('');
  const { t } = useTranslation();

  const handlePinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
    setPinCode(value);
    setError('');
  };

  const handleLogin = () => {
    if (pinCode.length !== 4) {
      setError(t('adjudicator.pin.invalidLength') as string);
      return;
    }

    const rejection = onSubmit(pinCode);
    if (rejection) {
      setError(rejection);
      setPinCode('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && pinCode.length === 4) {
      handleLogin();
    }
  };

  return (
    <Container fixed>
      <Box
        sx={{
          marginTop: '2em',
          height: '100%',
          flexDirection: 'column',
          justifyContent: 'center',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <Stack spacing={3} sx={{ width: '100%', maxWidth: 400, alignItems: 'center' }}>
          <Typography variant="h4" gutterBottom sx={{
            textAlign: "center"
          }}>
            {title}
          </Typography>
          <TextField
            value={pinCode}
            onChange={handlePinChange}
            onKeyDown={handleKeyDown}
            placeholder="----"
            slotProps={{
              htmlInput: {
                maxLength: 4,
                style: {
                  textAlign: 'center',
                  fontSize: '2rem',
                  letterSpacing: '0.5em',
                  fontFamily: 'monospace'
                },
                inputMode: 'numeric',
                pattern: '[0-9]*'
              }
            }}
            variant="outlined"
            fullWidth
            autoFocus
            error={!!error}
            helperText={error}
          />
          <Button
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            onClick={handleLogin}
            disabled={pinCode.length !== 4}
          >
            {t('adjudicator.pin.login')}
          </Button>
          {notice && (
            <Alert severity="info" variant="filled">
              {notice}
            </Alert>
          )}
        </Stack>
      </Box>
    </Container>
  );
};

export default PinEntry;
